import { ethers } from 'ethers';
import eventTicketArtifact from '@/abi/EventTicket.json';

export async function deployEventTicket({
  name,
  description,
  ticketPrice,
  maxTickets,
  signer,
}: {
  name: string;
  description: string;
  ticketPrice: string | number;
  maxTickets: string | number;
  signer: ethers.Signer;
}): Promise<string> {
  try {
    console.log('Deploying EventTicket contract with parameters:', {
      name,
      description,
      ticketPrice,
      maxTickets
    });

    // Validate parameters
    if (!name || !description) {
      throw new Error('Name and description are required');
    }

    // Convert parameters to proper types
    const price = typeof ticketPrice === 'string' ? BigInt(ticketPrice) : BigInt(ticketPrice);
    const maxTix = typeof maxTickets === 'string' ? BigInt(maxTickets) : BigInt(maxTickets);

    if (price < BigInt(0) || maxTix <= BigInt(0)) {
      throw new Error('Invalid price or max tickets value');
    }

    const factory = new ethers.ContractFactory(
      eventTicketArtifact.abi,
      eventTicketArtifact.bytecode,
      signer
    );

    // Create deployment transaction with explicit gas limit
    const deployTransaction = await factory.getDeployTransaction(name, description, price, maxTix);
    console.log('Deploy transaction:', deployTransaction);
    // Estimate gas with error handling
    let estimatedGas;
    try {
      estimatedGas = await signer.estimateGas(deployTransaction);
      console.log('Estimated gas:', estimatedGas.toString());
    } catch (gasError) {
      console.warn('Gas estimation failed, using default:', gasError);
      estimatedGas = BigInt(3000000); // Default gas limit
    }

    // Add 20% buffer to gas limit
    const gasLimit = estimatedGas * BigInt(120) / BigInt(100);
    console.log('Using gas limit:', gasLimit.toString());

    // Deploy with explicit gas limit
    const contract = await factory.deploy(
      name, 
      description, 
      price, 
      maxTix,
      { 
        gasLimit
      }
    );

    console.log('Deployment transaction hash:', contract.deploymentTransaction()?.hash);
    
    // Wait for deployment
    await contract.waitForDeployment();
    
    const contractAddress = await contract.getAddress();
    console.log('Contract deployed to:', contractAddress);
    
    return contractAddress;

  } catch (error) {
    console.error('Deployment failed:', error);
    
    // Provide more specific error messages
    if (error instanceof Error) {
      if (error.message.includes('insufficient funds')) {
        throw new Error('Insufficient funds for deployment. Please ensure your wallet has enough ETH for gas fees.');
      } else if (error.message.includes('user rejected')) {
        throw new Error('Transaction was rejected by user.');
      } else if (error.message.includes('nonce')) {
        throw new Error('Transaction nonce error. Please try again.');
      } else if (error.message.includes('gas')) {
        throw new Error('Gas estimation failed. Please try again or check your network connection.');
      } else if (error.message.includes('Internal JSON-RPC error')) {
        throw new Error('Network error. Please check your wallet connection and try again.');
      } else {
        throw new Error(`Deployment failed: ${error.message}`);
      }
    } else {
      throw new Error('An unexpected error occurred during deployment.');
    }
  }
} 